import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  FlatList,
  ScrollView,
  TouchableOpacity,
  Modal,
  TextInput,
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  Switch,
  useWindowDimensions,
  Alert,
  Image,
  useColorScheme,
  type ViewToken,
} from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BadgeChips } from '../../components/BadgeChips';
import { OverflowMenu } from '../../components/OverflowMenu';
import { ReportContentModal } from '../../components/ReportContentModal';
import { MarketplaceGridSkeleton } from '../../components/skeletons/ScreenSkeletons';
import { SearchLocationBanner } from '../../components/SearchLocationBanner';
import { SearchLocationPickerModal } from '../../components/SearchLocationPickerModal';
import { ModalBottomSheet } from '../../components/ModalBottomSheet';
import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { useMiniProfiles } from '../../hooks/useMiniProfiles';
import { useSearchLocationFilter } from '../../hooks/useSearchLocationFilter';
import {
  createListing,
  getMarketplaceListings,
  getMarketplaceListingsBySeller,
  setListingInactive,
  updateListing,
} from '../../services/communityService';
import { submitReport } from '../../services/reportService';
import { maybeAwardTrustedSellerBadge } from '../../services/badgeSyncService';
import { beautifyMarketplaceListingDescription } from '../../services/geminiService';
import { ensureChat } from '../../services/chatService';
import {
  blockUser,
  getBlockedUserIdsSet,
  getUserProfile,
  isProfileComplete,
} from '../../services/userService';
import {
  MARKETPLACE_CATEGORIES,
  type MarketplaceCategory,
  type MarketplaceListingDoc,
} from '../../types/community';
import {
  assertCloudinaryConfigured,
  cloudinaryDisplayUrl,
  uploadListingImages,
  type PickedListingMedia,
} from '../../services/cloudinaryService';
import { pickListingMedia } from '../../utils/pickListingMedia';
import { impactLight } from '../../utils/haptics';

function formatPrice(item: MarketplaceListingDoc): string {
  if (item.isDonation) {
    return 'Bağış';
  }
  if (item.price <= 0) {
    return 'Ücretsiz';
  }
  return `${item.price} ₺`;
}

function isActiveListing(item: MarketplaceListingDoc): boolean {
  return item.status !== 'sold' && item.status !== 'inactive';
}

/** Kart gövdesi: görsel + metin alanı sabit yükseklik */
const LISTING_CARD_IMAGE_HEIGHT = 140;
const LISTING_CARD_TOTAL_HEIGHT = 348;

export function MarketplaceScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const colorScheme = useColorScheme();
  const { user, profile, needsOnboarding, logout, refreshProfile } = useAuth();
  const isGuest = Boolean(user?.isAnonymous);
  const guestLocationPromptedRef = useRef(false);
  const {
    profileCity,
    profileDistrict,
    effectiveCity,
    effectiveDistrict,
    canSearch,
    isTemporaryFilter,
    pickerOpen,
    setPickerOpen,
    draftCity,
    setDraftCity,
    draftDistrict,
    setDraftDistrict,
    openPicker,
    applyPicker,
    clearFilter,
  } = useSearchLocationFilter(profile);

  useFocusEffect(
    useCallback(() => {
      if (isGuest && !guestLocationPromptedRef.current) {
        guestLocationPromptedRef.current = true;
        setPickerOpen(true);
      }
    }, [isGuest, setPickerOpen]),
  );

  const [listings, setListings] = useState<MarketplaceListingDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openingChatId, setOpeningChatId] = useState<string | null>(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftPrice, setDraftPrice] = useState('');
  const [draftCategory, setDraftCategory] = useState<MarketplaceCategory>('Giyim');
  const [draftDonation, setDraftDonation] = useState(false);
  const [draftTradeable, setDraftTradeable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [beautifyBusy, setBeautifyBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [securityBanner, setSecurityBanner] = useState<string | null>(null);
  const [draftListingPhotos, setDraftListingPhotos] = useState<PickedListingMedia[]>([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceListingDoc | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editCategory, setEditCategory] = useState<MarketplaceCategory>('Giyim');
  const [editDonation, setEditDonation] = useState(false);
  const [editTradeable, setEditTradeable] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [imageViewer, setImageViewer] = useState<{ urls: string[]; index: number } | null>(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [reportListing, setReportListing] = useState<MarketplaceListingDoc | null>(null);
  const [detailListing, setDetailListing] = useState<MarketplaceListingDoc | null>(null);
  /** Şikayet sonrası liste/refresh bu ilanları tekrar göstermesin (yalnızca şikayet eden oturumu). */
  const hiddenListingIdsByReporterRef = useRef<Set<string>>(new Set());

  const canCreateListing = Boolean(profileCity?.trim());
  const messageSendSpinnerColor = colorScheme === 'dark' ? '#18181b' : '#ffffff';

  const sellerIdsForMini = useMemo(() => listings.map(l => l.sellerId), [listings]);
  const sellerMiniProfiles = useMiniProfiles(sellerIdsForMini);

  const profileOk = useMemo(
    () => !needsOnboarding && isProfileComplete(profile),
    [needsOnboarding, profile],
  );
  const phoneVerified = Boolean(user?.phoneNumber);

  const blockedUserIds = useMemo(() => getBlockedUserIdsSet(profile), [profile?.blockedUsers]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (mineOnly) {
        if (!user?.uid) {
          setListings([]);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        if (!opts?.silent) {
          setLoading(true);
        }
        try {
          const list = await getMarketplaceListingsBySeller(user.uid);
          setListings(
            list.filter(
              l =>
                !hiddenListingIdsByReporterRef.current.has(l.id) && !blockedUserIds.has(l.sellerId),
            ),
          );
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
        return;
      }

      if (!canSearch) {
        setListings([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const list = await getMarketplaceListings(
          effectiveCity,
          effectiveDistrict.trim() ? effectiveDistrict : undefined,
        );
        setListings(
          list.filter(
            l =>
              !hiddenListingIdsByReporterRef.current.has(l.id) && !blockedUserIds.has(l.sellerId),
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [mineOnly, user?.uid, canSearch, effectiveCity, effectiveDistrict, blockedUserIds],
  );

  useEffect(() => {
    void load({ silent: true });
  }, [load]);

  useEffect(() => {
    if (imageViewer) {
      setGalleryIndex(imageViewer.index);
    }
  }, [imageViewer]);

  const galleryViewabilityConfig = useMemo(() => ({ itemVisiblePercentThreshold: 55 }), []);

  const onGalleryViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      const idx = viewableItems.find(v => v.isViewable)?.index;
      if (typeof idx === 'number') {
        setGalleryIndex(idx);
      }
    },
    [],
  );

  function onPressIlanVer() {
    void impactLight();
    setSecurityBanner(null);
    if (isGuest) {
      showGuestAlert(logout);
      return;
    }
    if (!profileOk) {
      setSecurityBanner(
        'İlan verebilmek için profilini tamamlaman gerekiyor. Ayarlardan eksik bilgileri doldur.',
      );
      return;
    }
    if (!phoneVerified) {
      setSecurityBanner(
        'Güvenlik için telefon numaranı doğrulaman gerekiyor. Giriş bölümünden SMS doğrulamasını tamamla.',
      );
      return;
    }
    setFormError(null);
    setDraftListingPhotos([]);
    setModalOpen(true);
  }

  function closeCreateModal() {
    if (submitting || beautifyBusy) {
      return;
    }
    setDraftListingPhotos([]);
    setModalOpen(false);
  }

  async function onAddListingPhoto() {
    if (draftListingPhotos.length >= 3) {
      return;
    }
    const picked = await pickListingMedia(3 - draftListingPhotos.length);
    if (!picked) {
      return;
    }
    setDraftListingPhotos(prev => [...prev, picked].slice(0, 3));
  }

  function onRemoveListingPhoto(index: number) {
    setDraftListingPhotos(prev => prev.filter((_, i) => i !== index));
  }

  const canBeautifyDescription = draftDescription.trim().length > 0;

  async function onBeautifyDescription() {
    if (!canBeautifyDescription || beautifyBusy || submitting) {
      return;
    }
    setFormError(null);
    setBeautifyBusy(true);
    try {
      const next = await beautifyMarketplaceListingDescription(draftDescription);
      setDraftDescription(next);
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Metin güzelleştirilemedi.');
    } finally {
      setBeautifyBusy(false);
    }
  }

  async function onSubmitListing() {
    setFormError(null);
    if (!user?.uid || !canCreateListing) {
      setFormError('Profil konumunda en az il bilgisi gerekli.');
      return;
    }
    const title = draftTitle.trim();
    const description = draftDescription.trim();
    if (!title || !description) {
      setFormError('Başlık ve açıklama zorunlu.');
      return;
    }
    if (!draftDonation) {
      const n = Number(draftPrice.replace(',', '.'));
      if (Number.isNaN(n) || n < 0) {
        setFormError('Geçerli bir fiyat gir veya Bağış seçeneğini aç.');
        return;
      }
    }
    if (draftListingPhotos.length < 1 || draftListingPhotos.length > 3) {
      setFormError('En az 1, en fazla 3 fotoğraf seçmelisin.');
      return;
    }
    try {
      assertCloudinaryConfigured();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Cloudinary yapılandırması eksik.');
      return;
    }
    setSubmitting(true);
    try {
      const priceNum = draftDonation ? 0 : Math.round(Number(draftPrice.replace(',', '.')));
      const imageUrls = await uploadListingImages(draftListingPhotos);
      await createListing(user.uid, {
        title,
        description,
        category: draftCategory,
        price: priceNum,
        isDonation: draftDonation,
        isTradeable: draftTradeable,
        city: profileCity,
        district: profileDistrict.trim(),
        images: imageUrls,
      });
      setModalOpen(false);
      setDraftTitle('');
      setDraftDescription('');
      setDraftPrice('');
      setDraftCategory('Giyim');
      setDraftDonation(false);
      setDraftTradeable(false);
      setDraftListingPhotos([]);
      await load({ silent: true });
      void maybeAwardTrustedSellerBadge(user.uid);
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  }

  function openEdit(item: MarketplaceListingDoc) {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditDescription(item.description);
    setEditPrice(item.isDonation ? '' : String(item.price));
    setEditCategory(item.category as MarketplaceCategory);
    setEditDonation(item.isDonation);
    setEditTradeable(Boolean(item.isTradeable));
    setEditError(null);
    setEditModalOpen(true);
  }

  async function onSubmitEdit() {
    if (!user?.uid || !editingItem) {
      return;
    }
    const title = editTitle.trim();
    const description = editDescription.trim();
    if (!title || !description) {
      setEditError('Başlık ve açıklama zorunlu.');
      return;
    }
    if (!editDonation) {
      const n = Number(editPrice.replace(',', '.'));
      if (Number.isNaN(n) || n < 0) {
        setEditError('Geçerli fiyat veya bağış seçeneği.');
        return;
      }
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      const priceNum = editDonation ? 0 : Math.round(Number(editPrice.replace(',', '.')));
      await updateListing(editingItem.id, user.uid, {
        title,
        description,
        category: editCategory,
        price: priceNum,
        isDonation: editDonation,
        isTradeable: editTradeable,
      });
      setEditModalOpen(false);
      setEditingItem(null);
      await load({ silent: true });
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : 'Güncellenemedi.');
    } finally {
      setEditSubmitting(false);
    }
  }

  function confirmRemove(item: MarketplaceListingDoc) {
    if (!user?.uid) {
      return;
    }
    Alert.alert(
      'Satıştan kaldır',
      'Bu ilan listeden kaldırılacak. Emin misin?',
      [
        { text: 'Vazgeç', style: 'cancel' },
        {
          text: 'Kaldır',
          style: 'destructive',
          onPress: () => void removeListing(item),
        },
      ],
    );
  }

  async function removeListing(item: MarketplaceListingDoc) {
    if (!user?.uid) {
      return;
    }
    setRemovingId(item.id);
    try {
      await setListingInactive(item.id, user.uid);
      await load({ silent: true });
    } catch {
      Alert.alert('Hata', 'İlan kaldırılamadı.');
    } finally {
      setRemovingId(null);
    }
  }

  async function onMessageSeller(item: MarketplaceListingDoc) {
    void impactLight();
    if (user?.isAnonymous === true) {
      showGuestAlert(logout);
      return;
    }
    if (!user?.uid) {
      return;
    }
    if (item.sellerId === user.uid) {
      return;
    }
    setOpeningChatId(item.id);
    try {
      const seller = await getUserProfile(item.sellerId);
      const peerName = seller?.displayName?.trim() || 'Annedaş';
      const { chatId } = await ensureChat(user.uid, item.sellerId);
      navigation.dispatch(
        CommonActions.navigate({
          name: 'Chat',
          params: {
            chatId,
            otherUserId: item.sellerId,
            peerName,
            initialMessage: `Merhaba, "${item.title}" ilanıyla ilgileniyorum.`,
            hideIcebreakers: true,
            autoSendInitialDraft: false,
          },
        }),
      );
    } finally {
      setOpeningChatId(null);
    }
  }

  function openListingPhotos(item: MarketplaceListingDoc) {
    const ids = item.images ?? [];
    if (ids.length === 0) {
      return;
    }
    void impactLight();
    setImageViewer({
      urls: ids.map(id => cloudinaryDisplayUrl(id, 900)),
      index: 0,
    });
  }

  const cardWidth = (width - 40 - 12) / 2;

  function renderEmpty() {
    if (mineOnly) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-950 px-5 py-8 ">
 <Text className="text-center text-base font-semibold text-gray-800">
            Henüz ilan vermedin
          </Text>
 <Text className="mt-2 text-center text-sm text-gray-600">
            İlan Ver ile ekleyebilirsin.
          </Text>
        </View>
      );
    }
    if (!canSearch) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-white p-6 ">
 <Text className="text-center text-sm leading-6 text-gray-600">
            Listeyi görmek için yukarıdan{' '}
 <Text className="font-semibold">
              {isGuest ? 'Konum Seç' : 'Konum Değiştir'}
            </Text>{' '}
            ile en az il seçebilirsin
            {isGuest ? '.' : ' veya Ana Sayfa → Profilim’den kalıcı adresini kaydedebilirsin.'}
          </Text>
        </View>
      );
    }
    if (isTemporaryFilter) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-950 px-5 py-8 ">
 <Text className="text-center text-base font-semibold text-gray-800">
            Seçtiğin bu bölgede henüz ilan yok.
          </Text>
 <Text className="mt-2 text-center text-sm leading-6 text-gray-600">
            Kendi bölgene dönmek ister misin?
          </Text>
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={clearFilter}
 className="mt-5 self-center rounded-2xl bg-zinc-900 dark:bg-zinc-50 px-6 py-3">
 <Text className="text-center text-sm font-bold text-white">
              {isGuest ? 'Konum seçimini temizle' : 'Profil konumuma dön'}
            </Text>
          </TouchableOpacity>
        </View>
      );
    }
    return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-950 px-5 py-8 ">
 <Text className="text-center text-base font-semibold text-gray-800">
          Henüz ilan yok
        </Text>
 <Text className="mt-2 text-center text-sm leading-6 text-gray-600">
 İlan vermek için <Text className="font-semibold">İlanlarım</Text> sekmesine geç.
        </Text>
      </View>
    );
  }

  const showLoader = loading && listings.length === 0 && (mineOnly ? Boolean(user?.uid) : canSearch);

  if (showLoader) {
    return (
 <View className="flex-1 bg-white dark:bg-black">
        <MarketplaceGridSkeleton cardWidth={cardWidth} />
      </View>
    );
  }

  return (
 <View className="flex-1 bg-white dark:bg-black">
      {securityBanner ? (
        <View
 className="border-b border-zinc-200 bg-zinc-100 dark:bg-zinc-900 px-4 py-3"
          style={{ paddingTop: 12 }}>
 <Text className="text-center text-sm leading-5 text-zinc-900">{securityBanner}</Text>
 <TouchableOpacity onPress={() => setSecurityBanner(null)} className="mt-2 self-center">
 <Text className="text-xs font-semibold text-zinc-700">Kapat</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <View className="px-5 pt-4">
        <View className="mb-1 flex-row border-b border-zinc-200 dark:border-zinc-800">
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={() => setMineOnly(false)}
            className={`mr-6 flex-1 pb-2.5 ${
              !mineOnly
                ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                : 'border-b-2 border-transparent'
            }`}>
            <Text
              className={`text-center text-sm font-semibold ${
                !mineOnly ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
              }`}>
              Tüm ilanlar
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={() => setMineOnly(true)}
            className={`flex-1 pb-2.5 ${
              mineOnly
                ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                : 'border-b-2 border-transparent'
            }`}>
            <Text
              className={`text-center text-sm font-semibold ${
                mineOnly ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
              }`}>
              İlanlarım
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View className="px-5 pb-2 pt-3">
        {!mineOnly ? (
          <SearchLocationBanner
            theme="rose"
            effectiveCity={effectiveCity}
            effectiveDistrict={effectiveDistrict}
            isTemporaryFilter={isTemporaryFilter}
            onChangePress={openPicker}
            onClearFilter={clearFilter}
            isGuest={isGuest}
          />
        ) : (
          <View className="mb-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="text-xs font-medium text-gray-500 dark:text-zinc-400">İlanların</Text>
            <Text className="mt-1 text-sm font-semibold text-gray-800 dark:text-zinc-100">
              Yayında ve kaldırdığın ilanlar
            </Text>
          </View>
        )}

        {mineOnly ? (
          <TouchableOpacity
            activeOpacity={0.9}
            onPress={onPressIlanVer}
            className="rounded-xl border border-zinc-200 bg-zinc-50 px-5 py-3 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="text-center text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              İlan Ver
            </Text>
            <Text className="mt-1 text-center text-xs text-zinc-900/80 dark:text-zinc-200/80">
              Bebek eşyası sat veya bağışla
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>

      <FlatList
        data={listings}
        keyExtractor={item => item.id}
        numColumns={2}
        keyboardShouldPersistTaps="handled"
        columnWrapperStyle={{ gap: 12, marginBottom: 12 }}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 8,
          paddingBottom: 32 + insets.bottom + 96,
        }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void load({ silent: true });
            }}
            tintColor="#18181b"
          />
        }
        ListEmptyComponent={renderEmpty}
        renderItem={({ item }) => {
          const mine = item.sellerId === user?.uid;
          const active = isActiveListing(item);
          const coverId = item.images?.[0];
          const hasPhoto = Boolean(coverId);
          const contentMinH = LISTING_CARD_TOTAL_HEIGHT - LISTING_CARD_IMAGE_HEIGHT;
          return (
            <View style={{ width: cardWidth, height: LISTING_CARD_TOTAL_HEIGHT }}>
              <Pressable
                onPress={() => setDetailListing(item)}
                accessibilityRole="button"
                accessibilityLabel={`${item.title}, ilan detayı`}
                className="h-full overflow-hidden rounded-2xl border border-zinc-200 bg-white active:opacity-95 dark:border-zinc-800 dark:bg-zinc-900">
                {hasPhoto && coverId ? (
                  <View className="relative w-full" style={{ height: LISTING_CARD_IMAGE_HEIGHT }}>
                    <Image
                      source={{ uri: cloudinaryDisplayUrl(coverId, 500) }}
                      style={{ width: '100%', height: LISTING_CARD_IMAGE_HEIGHT }}
                      className="rounded-t-2xl"
                      resizeMode="cover"
                    />
                    {item.isTradeable ? (
                      <View
                        pointerEvents="none"
                        className="absolute left-2 top-2 rounded-full border border-zinc-200 bg-zinc-100 px-2.5 py-1 dark:border-zinc-700 dark:bg-zinc-800">
                        <Text className="text-[10px] font-bold text-zinc-900 dark:text-zinc-100">
                          🔄 Takasa Açık
                        </Text>
                      </View>
                    ) : null}
                  </View>
                ) : (
                  <View
                    className="w-full items-center justify-center rounded-t-2xl bg-zinc-100 dark:bg-zinc-800"
                    style={{ height: LISTING_CARD_IMAGE_HEIGHT }}>
                    <Text className="text-xs text-zinc-400 dark:text-zinc-500">Fotoğraf yok</Text>
                    {item.isTradeable ? (
                      <View className="absolute left-2 top-2 rounded-full border border-zinc-200 bg-zinc-100 px-2.5 py-0.5 dark:border-zinc-700 dark:bg-zinc-800">
                        <Text className="text-[10px] font-bold text-zinc-800 dark:text-zinc-100">
                          🔄 Takasa Açık
                        </Text>
                      </View>
                    ) : null}
                  </View>
                )}
                <View className="flex-1 justify-between px-3 pb-3 pt-1.5" style={{ minHeight: contentMinH }}>
                  <View>
                    <View className="mb-0.5 flex-row flex-wrap items-center gap-2">
                      <Text className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                        {item.category}
                      </Text>
                    </View>
                    <Text
                      className="text-base font-semibold leading-5 text-gray-900 dark:text-zinc-100"
                      numberOfLines={2}>
                      {item.title}
                    </Text>
                    <View className="mt-0.5 flex-row flex-wrap items-center gap-x-1">
                      <Text
                        className="text-xs text-gray-500 dark:text-zinc-400"
                        numberOfLines={1}>
                        {sellerMiniProfiles[item.sellerId]?.displayName ?? 'Satıcı'}
                      </Text>
                      <BadgeChips badges={sellerMiniProfiles[item.sellerId]?.badges} compact />
                    </View>
                    <Text className="text-lg font-bold text-zinc-600 dark:text-zinc-400">
                      {formatPrice(item)}
                    </Text>
                    <Text className="text-xs text-gray-500 dark:text-zinc-400" numberOfLines={1}>
                      {item.district ? `${item.district}` : item.city}
                    </Text>
                  </View>
                  <View className="mt-2">
                    {!active ? (
                      <View className="rounded-xl bg-gray-100 px-2 py-2 dark:bg-zinc-800">
                        <Text className="text-center text-xs font-medium text-gray-500 dark:text-zinc-400">
                          Satıştan kaldırıldı
                        </Text>
                      </View>
                    ) : mine ? (
                      <View className="gap-2">
                        <TouchableOpacity
                          activeOpacity={0.88}
                          onPress={() => openEdit(item)}
                          className="rounded-xl border border-zinc-300 bg-white px-2 py-2 dark:border-zinc-600 dark:bg-zinc-950">
                          <Text className="text-center text-xs font-bold text-zinc-700 dark:text-zinc-200">
                            Düzenle
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          disabled={removingId === item.id}
                          activeOpacity={0.88}
                          onPress={() => confirmRemove(item)}
                          className="rounded-xl border border-gray-200 bg-gray-50 px-2 py-2 dark:border-zinc-700 dark:bg-zinc-800">
                          {removingId === item.id ? (
                            <ActivityIndicator size="small" color="#9ca3af" />
                          ) : (
                            <Text className="text-center text-xs font-bold text-gray-600 dark:text-zinc-300">
                              Satıştan Kaldır
                            </Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TouchableOpacity
                        disabled={openingChatId === item.id}
                        onPress={() => {
                          if (user?.isAnonymous === true) {
                            showGuestAlert(logout);
                            return;
                          }
                          void onMessageSeller(item);
                        }}
                        className="rounded-xl bg-zinc-900 px-3 py-2.5 active:opacity-90 dark:bg-zinc-50">
                        {openingChatId === item.id ? (
                          <ActivityIndicator color={messageSendSpinnerColor} size="small" />
                        ) : (
                          <Text className="text-center text-xs font-bold text-white dark:text-zinc-950">
                            Mesaj Gönder
                          </Text>
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              </Pressable>
            </View>
          );
        }}
      />

      <Modal
        visible={detailListing !== null}
        animationType="slide"
        {...(Platform.OS === 'ios' ? { presentationStyle: 'pageSheet' as const } : {})}
        onRequestClose={() => setDetailListing(null)}>
        {detailListing ? (
          <View className="flex-1 bg-white dark:bg-black">
            <View style={{ paddingTop: insets.top }} className="border-b border-zinc-200 dark:border-zinc-800">
              <View className="flex-row items-center px-2 py-2">
                <Pressable
                  hitSlop={8}
                  onPress={() => setDetailListing(null)}
                  className="min-w-[72px] px-2 py-1">
                  <Text className="text-base font-semibold text-zinc-600 dark:text-zinc-300">Kapat</Text>
                </Pressable>
                <Text
                  className="flex-1 text-center text-base font-bold text-zinc-900 dark:text-zinc-50"
                  numberOfLines={1}>
                  İlan
                </Text>
                <View className="min-w-[72px] items-end">
                  {detailListing.sellerId !== user?.uid &&
                  isActiveListing(detailListing) &&
                  user?.uid &&
                  !user.isAnonymous ? (
                    <OverflowMenu
                      accessibilityLabel="İlan seçenekleri"
                      items={[
                        {
                          key: 'report-listing',
                          label: 'İlanı şikayet et',
                          destructive: true,
                          onPress: () => {
                            const listing = detailListing;
                            if (!listing) {
                              return;
                            }
                            setDetailListing(null);
                            // pageSheet / detay Modal kapanırken hemen şikayet Modal’ı açılınca iOS’ta sheet görünmeyebilir.
                            setTimeout(() => setReportListing(listing), 420);
                          },
                        },
                        {
                          key: 'block-seller',
                          label: 'Kullanıcıyı engelle',
                          destructive: true,
                          onPress: () => {
                            const sellerId = detailListing.sellerId;
                            if (!user?.uid) {
                              return;
                            }
                            Alert.alert(
                              'Kullanıcıyı engelle',
                              'Bu satıcının ilanları sende görünmez.',
                              [
                                { text: 'Vazgeç', style: 'cancel' },
                                {
                                  text: 'Engelle',
                                  style: 'destructive',
                                  onPress: () => {
                                    void (async () => {
                                      try {
                                        await blockUser(user.uid, sellerId);
                                        await refreshProfile();
                                        setListings(prev => prev.filter(l => l.sellerId !== sellerId));
                                        setDetailListing(null);
                                        Alert.alert('Engellendi', 'Bu satıcının ilanları gizlendi.');
                                      } catch (e) {
                                        Alert.alert(
                                          'Hata',
                                          e instanceof Error ? e.message : 'Engellenemedi.',
                                        );
                                      }
                                    })();
                                  },
                                },
                              ],
                            );
                          },
                        },
                      ]}
                    />
                  ) : null}
                </View>
              </View>
            </View>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              className="flex-1 px-5 pt-4"
              contentContainerStyle={{ paddingBottom: 24 }}>
              {detailListing.images && detailListing.images.length > 0 ? (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  className="mb-4 -mx-1">
                  {detailListing.images.map((imgId, idx) => (
                    <Pressable
                      key={`${detailListing.id}-${idx}`}
                      onPress={() => openListingPhotos(detailListing)}
                      className="mr-2 overflow-hidden rounded-xl">
                      <Image
                        source={{ uri: cloudinaryDisplayUrl(imgId, 720) }}
                        style={{ width: Math.min(width * 0.72, 320), height: Math.min(width * 0.48, 220) }}
                        className="rounded-xl bg-zinc-100 dark:bg-zinc-800"
                        resizeMode="cover"
                      />
                    </Pressable>
                  ))}
                </ScrollView>
              ) : null}
              <View className="mb-2 flex-row flex-wrap gap-2">
                <View className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 dark:border-zinc-700 dark:bg-zinc-900">
                  <Text className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                    {detailListing.category}
                  </Text>
                </View>
                {detailListing.isTradeable ? (
                  <View className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 dark:border-amber-900/50 dark:bg-amber-950/40">
                    <Text className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                      Takasa açık
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{detailListing.title}</Text>
              <Text className="mt-2 text-2xl font-bold text-zinc-700 dark:text-zinc-300">
                {formatPrice(detailListing)}
              </Text>
              <Text className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
                {sellerMiniProfiles[detailListing.sellerId]?.displayName ?? 'Satıcı'}
              </Text>
              <View className="mt-1 flex-row flex-wrap items-center gap-x-1">
                <BadgeChips badges={sellerMiniProfiles[detailListing.sellerId]?.badges} compact />
              </View>
              <Text className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
                {detailListing.district
                  ? `${detailListing.district}, ${detailListing.city}`
                  : detailListing.city}
              </Text>
              {detailListing.condition ? (
                <Text className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
                  Durum: {detailListing.condition}
                </Text>
              ) : null}
              <Text className="mt-5 text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Açıklama
              </Text>
              <Text className="mt-1 text-base leading-6 text-zinc-800 dark:text-zinc-200">
                {detailListing.description}
              </Text>
            </ScrollView>
            {detailListing.sellerId !== user?.uid && isActiveListing(detailListing) ? (
              <View
                className="border-t border-zinc-200 bg-white px-5 py-3 dark:border-zinc-800 dark:bg-zinc-950"
                style={{ paddingBottom: Math.max(insets.bottom, 12) }}>
                <TouchableOpacity
                  disabled={openingChatId === detailListing.id}
                  onPress={() => {
                    if (user?.isAnonymous === true) {
                      showGuestAlert(logout);
                      return;
                    }
                    void onMessageSeller(detailListing);
                    setDetailListing(null);
                  }}
                  className="rounded-2xl bg-zinc-900 py-3.5 dark:bg-zinc-50">
                  {openingChatId === detailListing.id ? (
                    <ActivityIndicator color={messageSendSpinnerColor} />
                  ) : (
                    <Text className="text-center text-base font-bold text-white dark:text-zinc-950">
                      Satıcıya mesaj gönder
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        ) : null}
      </Modal>

      <ModalBottomSheet
        visible={modalOpen}
        onRequestClose={closeCreateModal}
        dismissDisabled={submitting || beautifyBusy}
        keyboardAvoiding>
        <View
          className="max-h-[90%] rounded-t-3xl border border-zinc-200 bg-white px-5 pt-4"
          style={{ paddingBottom: 24 + insets.bottom }}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <View className="mb-4 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700" />
              <View className="mb-4 border-b border-zinc-200 pb-4 dark:border-zinc-700">
                <Text className="text-lg font-bold text-gray-900 dark:text-zinc-100">Yeni ilan</Text>
              </View>

              <Text className="mb-2 text-xs font-medium text-gray-500">
                Fotoğraflar (zorunlu, 1–3)
              </Text>
 <View className="mb-4 flex-row gap-2">
                {[0, 1, 2].map(slot => {
                  const photo = draftListingPhotos[slot];
                  const isNextSlot = slot === draftListingPhotos.length && draftListingPhotos.length < 3;
                  return (
                    <View
                      key={slot}
 className="h-24 flex-1 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/70">
                      {photo ? (
 <View className="relative h-full w-full">
                          <Image
                            source={{ uri: photo.uri }}
 className="h-full w-full"
                            resizeMode="cover"
                          />
                          <TouchableOpacity
                            onPress={() => onRemoveListingPhoto(slot)}
 className="absolute right-1 top-1 rounded-full bg-black/50 px-2 py-0.5">
 <Text className="text-xs font-bold text-white">✕</Text>
                          </TouchableOpacity>
                        </View>
                      ) : isNextSlot ? (
                        <TouchableOpacity
                          activeOpacity={0.85}
                          disabled={submitting || beautifyBusy}
                          onPress={() => void onAddListingPhoto()}
 className="h-full w-full items-center justify-center">
 <Text className="text-2xl text-zinc-400">+</Text>
 <Text className="text-[10px] font-medium text-zinc-500">Ekle</Text>
                        </TouchableOpacity>
                      ) : (
 <View className="h-full w-full items-center justify-center opacity-40">
                          <Text className="text-lg text-zinc-300 dark:text-zinc-600">—</Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>

 <Text className="mb-1 text-xs font-medium text-gray-500">Başlık</Text>
              <TextInput
                value={draftTitle}
                onChangeText={setDraftTitle}
                placeholder="Örn. 0-3 ay tulum seti"
                placeholderTextColor="#9ca3af"
                className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
              />

              <Text className="mb-2 text-xs font-medium text-gray-500">Kategori</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                className="mb-3 border-b border-zinc-200 dark:border-zinc-700">
                <View className="flex-row pb-0">
                  {MARKETPLACE_CATEGORIES.map(cat => {
                    const selected = draftCategory === cat;
                    return (
                      <TouchableOpacity
                        key={cat}
                        onPress={() => setDraftCategory(cat)}
                        className={`mr-4 pb-2 ${
                          selected
                            ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                            : 'border-b-2 border-transparent'
                        }`}>
                        <Text
                          className={`text-xs font-semibold ${
                            selected
                              ? 'text-zinc-900 dark:text-zinc-100'
                              : 'text-zinc-500 dark:text-zinc-500'
                          }`}>
                          {cat}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>

              <View className="mb-3 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/60">
 <Text className="text-sm font-medium text-gray-700">Bağış / ücretsiz</Text>
                <Switch
                  value={draftDonation}
                  onValueChange={setDraftDonation}
                  trackColor={{ false: '#fecdd3', true: '#fda4af' }}
                  thumbColor={draftDonation ? '#fff' : '#f4f4f5'}
                />
              </View>

              {!draftDonation ? (
                <>
                  <Text className="mb-1 text-xs font-medium text-gray-500">Fiyat (TL)</Text>
                  <TextInput
                    value={draftPrice}
                    onChangeText={setDraftPrice}
                    placeholder="150"
                    placeholderTextColor="#9ca3af"
                    keyboardType="decimal-pad"
                    className="mb-2 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
                  />
                </>
              ) : null}

              <View className="mb-3 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/70">
                <View className="mr-3 flex-1">
                  <Text className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Takasa Açık
                  </Text>
 <Text className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                    Takas tekliflerine açık olduğunu göster
                  </Text>
                </View>
                <Switch
                  value={draftTradeable}
                  onValueChange={setDraftTradeable}
                  trackColor={{ false: '#99f6e4', true: '#2dd4bf' }}
                  thumbColor={draftTradeable ? '#fff' : '#f4f4f5'}
                />
              </View>

 <Text className="mb-1 text-xs font-medium text-gray-500">Açıklama</Text>
              <TextInput
                value={draftDescription}
                onChangeText={setDraftDescription}
                placeholder="Durum, ölçü, teslim şekli…"
                placeholderTextColor="#9ca3af"
                multiline
                editable={!beautifyBusy}
                className="mb-3 min-h-[88px] border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
                textAlignVertical="top"
              />

              <TouchableOpacity
                activeOpacity={0.88}
                disabled={!canBeautifyDescription || beautifyBusy || submitting}
                onPress={() => void onBeautifyDescription()}
                className={`mb-4 rounded-xl border px-3 py-2.5 ${
                  !canBeautifyDescription || submitting
                    ? 'border-zinc-200 bg-zinc-50 opacity-60 dark:border-zinc-800 dark:bg-zinc-900/60'
                    : 'border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800'
                }`}>
                {beautifyBusy ? (
 <View className="flex-row items-center justify-center gap-2">
                    <ActivityIndicator size="small" color="#18181b" />
 <Text className="text-sm font-semibold text-zinc-800">
                      Gemini güzelleştiriyor... 🪄
                    </Text>
                  </View>
                ) : (
 <Text className="text-center text-sm font-semibold text-zinc-700">
                    Metni Güzelleştir ✨
                  </Text>
                )}
              </TouchableOpacity>

 {formError ? <Text className="mb-3 text-sm text-zinc-700">{formError}</Text> : null}

              <TouchableOpacity
                disabled={submitting || beautifyBusy}
                onPress={() => void onSubmitListing()}
                className="mb-2 rounded-xl bg-zinc-900 py-3 active:opacity-90 dark:bg-zinc-50">
                {submitting ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">
                    Yayınla
                  </Text>
                )}
              </TouchableOpacity>
            </ScrollView>
          </View>
      </ModalBottomSheet>

      <ModalBottomSheet
        visible={editModalOpen}
        onRequestClose={() => !editSubmitting && setEditModalOpen(false)}
        dismissDisabled={editSubmitting}
        keyboardAvoiding>
        <View
          className="max-h-[85%] rounded-t-3xl border border-zinc-200 bg-white px-5 pt-4"
          style={{ paddingBottom: 24 + insets.bottom }}>
            <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              <View className="mb-4 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700" />
              <View className="mb-4 border-b border-zinc-200 pb-4 dark:border-zinc-700">
                <Text className="text-lg font-bold text-gray-900 dark:text-zinc-100">İlanı düzenle</Text>
              </View>

              <Text className="mb-1 text-xs font-medium text-gray-500">Başlık</Text>
              <TextInput
                value={editTitle}
                onChangeText={setEditTitle}
                className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
              />

              <Text className="mb-2 text-xs font-medium text-gray-500">Kategori</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                className="mb-3 border-b border-zinc-200 dark:border-zinc-700">
                <View className="flex-row">
                  {MARKETPLACE_CATEGORIES.map(cat => {
                    const selected = editCategory === cat;
                    return (
                      <TouchableOpacity
                        key={cat}
                        onPress={() => setEditCategory(cat)}
                        className={`mr-4 pb-2 ${
                          selected
                            ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                            : 'border-b-2 border-transparent'
                        }`}>
                        <Text
                          className={`text-xs font-semibold ${
                            selected
                              ? 'text-zinc-900 dark:text-zinc-100'
                              : 'text-zinc-500 dark:text-zinc-500'
                          }`}>
                          {cat}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>

              <View className="mb-3 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/60">
 <Text className="text-sm font-medium text-gray-700">Bağış / ücretsiz</Text>
                <Switch
                  value={editDonation}
                  onValueChange={setEditDonation}
                  trackColor={{ false: '#fecdd3', true: '#fda4af' }}
                  thumbColor={editDonation ? '#fff' : '#f4f4f5'}
                />
              </View>

              {!editDonation ? (
                <>
                  <Text className="mb-1 text-xs font-medium text-gray-500">Fiyat (TL)</Text>
                  <TextInput
                    value={editPrice}
                    onChangeText={setEditPrice}
                    keyboardType="decimal-pad"
                    className="mb-2 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
                  />
                </>
              ) : null}

              <View className="mb-3 flex-row items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-zinc-800 dark:bg-zinc-900/70">
                <View className="mr-3 flex-1">
                  <Text className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Takasa Açık
                  </Text>
 <Text className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                    Takas tekliflerine açık olduğunu göster
                  </Text>
                </View>
                <Switch
                  value={editTradeable}
                  onValueChange={setEditTradeable}
                  trackColor={{ false: '#99f6e4', true: '#2dd4bf' }}
                  thumbColor={editTradeable ? '#fff' : '#f4f4f5'}
                />
              </View>

 <Text className="mb-1 text-xs font-medium text-gray-500">Açıklama</Text>
              <TextInput
                value={editDescription}
                onChangeText={setEditDescription}
                multiline
                className="mb-3 min-h-[88px] border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
                textAlignVertical="top"
              />

              {editError ? <Text className="mb-3 text-sm text-zinc-700">{editError}</Text> : null}

              <TouchableOpacity
                disabled={editSubmitting}
                onPress={() => void onSubmitEdit()}
                className="mb-2 rounded-xl bg-zinc-900 py-3 active:opacity-90 dark:bg-zinc-50">
                {editSubmitting ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">
                    Kaydet
                  </Text>
                )}
              </TouchableOpacity>
            </ScrollView>
          </View>
      </ModalBottomSheet>

      <SearchLocationPickerModal
        visible={pickerOpen}
        theme="rose"
        title="Arama konumu"
        city={draftCity}
        district={draftDistrict}
        onCityChange={setDraftCity}
        onDistrictChange={setDraftDistrict}
        onApply={applyPicker}
        onClose={() => setPickerOpen(false)}
      />

      <Modal
        visible={imageViewer !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setImageViewer(null)}>
        <View className="flex-1 bg-black" style={{ paddingTop: insets.top }}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Kapat"
            onPress={() => setImageViewer(null)}
            className="z-10 self-end px-4 py-3 active:opacity-70">
            <Text className="text-base font-semibold text-white">Kapat</Text>
          </TouchableOpacity>
          {imageViewer && imageViewer.urls.length > 0 ? (
            <FlatList
              data={imageViewer.urls}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              keyExtractor={(_, i) => String(i)}
              initialScrollIndex={imageViewer.index}
              initialNumToRender={imageViewer.urls.length}
              getItemLayout={(_, index) => ({
                length: width,
                offset: width * index,
                index,
              })}
              viewabilityConfig={galleryViewabilityConfig}
              onViewableItemsChanged={onGalleryViewableItemsChanged}
              contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
              style={{ flex: 1 }}
              renderItem={({ item: uri }) => (
                <View style={{ width }} className="items-center justify-center pb-8">
                  <Image
                    source={{ uri }}
                    style={{ width: width - 32, height: width - 32, maxHeight: width * 1.15 }}
                    resizeMode="contain"
                  />
                </View>
              )}
            />
          ) : null}
          {imageViewer && imageViewer.urls.length > 1 ? (
            <View
              pointerEvents="none"
              className="absolute inset-x-0 items-center"
              style={{ bottom: Math.max(insets.bottom, 20) }}>
              <View className="rounded-full bg-white/15 px-4 py-2">
                <Text
                  className="text-sm font-semibold text-white"
                  accessibilityRole="text"
                  accessibilityLiveRegion="polite"
                  accessibilityLabel={`Fotoğraf ${galleryIndex + 1} / ${imageViewer.urls.length}`}>
                  {galleryIndex + 1} / {imageViewer.urls.length}
                </Text>
              </View>
            </View>
          ) : null}
        </View>
      </Modal>

      {reportListing && user?.uid && !user.isAnonymous ? (
        <ReportContentModal
          visible
          title="İlanı şikayet et"
          subtitle={`“${reportListing.title}”`}
          reportBase={{
            targetType: 'marketplace_listing',
            targetAuthorId: reportListing.sellerId,
            marketplaceListingId: reportListing.id,
          }}
          onClose={() => setReportListing(null)}
          onSubmit={async partial => {
            const listingId = reportListing.id;
            await submitReport({ ...partial, reporterId: user.uid });
            hiddenListingIdsByReporterRef.current.add(listingId);
            setListings(prev => prev.filter(l => l.id !== listingId));
            if (detailListing?.id === listingId) {
              setDetailListing(null);
            }
            Alert.alert(
              'Teşekkürler',
              'Şikayetin kaydedildi. Bu ilan senin ekranından kaldırıldı; incelenecek.',
            );
          }}
        />
      ) : null}
    </View>
  );
}
